// GATE-CLEAR-1 (s147) — the PLANNER confirm. When a planner edit would close a showIf gate on
// one or more NON-EMPTY dependent fields, the server replies 409 { needsGateClearConfirm, clears }
// instead of writing. This hook turns that 409 into a confirm dialog that NAMES what will be cleared
// ("Clear 6 details for Ava?") — a mis-tap on "has a guardian" must never silently wipe a child's
// guardianship details. On confirm the write is re-issued with confirmGateClear:true; on cancel the
// write is abandoned and the caller reverts the source field (nothing is cleared).
//
// Extraction + executor writers clear WITHOUT this prompt (no human is watching; the executor case
// is recorded through the change spine) — they never hit the 409.

import { useCallback, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Thrown by writeWithGateClear when the planner declines. Callers catch this to revert the source
 *  field silently (no error toast) — it is a choice, not a failure. */
export class GateClearCancelledError extends Error {
  constructor() {
    super("gate-clear-cancelled");
    this.name = "GateClearCancelledError";
  }
}

export type GateClearField = { fieldKey: string; label: string };

type Prompt = {
  clears: GateClearField[];
  recordLabel?: string;
  resolve: (ok: boolean) => void;
};

/** apiRequest throws `Error("409: <body>")`; parse the gate-clear confirmation out of it. */
export function parseGateClear409(e: unknown): { clears: GateClearField[] } | null {
  if (!(e instanceof Error)) return null;
  const m = /^409: ([\s\S]*)$/.exec(e.message);
  if (!m) return null;
  try {
    const body = JSON.parse(m[1]);
    return body?.needsGateClearConfirm && Array.isArray(body.clears) ? { clears: body.clears } : null;
  } catch {
    return null;
  }
}

export function useGateClearConfirm() {
  const [prompt, setPrompt] = useState<Prompt | null>(null);

  /**
   * Run a write. `doWrite(confirm)` should issue the request, adding `confirmGateClear: true` to the
   * body when `confirm` is true. On a gate-clear 409 the planner is asked first; on confirm the write
   * is retried with the flag, on cancel a GateClearCancelledError is thrown for the caller to revert.
   */
  const writeWithGateClear = useCallback(
    async <T,>(
      doWrite: (confirm: boolean) => Promise<T>,
      opts?: { recordLabel?: string },
    ): Promise<T> => {
      try {
        return await doWrite(false);
      } catch (e) {
        const gc = parseGateClear409(e);
        if (!gc) throw e;
        const ok = await new Promise<boolean>((resolve) =>
          setPrompt({ clears: gc.clears, recordLabel: opts?.recordLabel, resolve }),
        );
        setPrompt(null);
        if (!ok) throw new GateClearCancelledError();
        return await doWrite(true);
      }
    },
    [],
  );

  const count = prompt?.clears.length ?? 0;
  const noun = count === 1 ? "detail" : "details";
  const forWhom = prompt?.recordLabel ? ` for ${prompt.recordLabel}` : "";

  const dialog = (
    <AlertDialog
      open={!!prompt}
      onOpenChange={(open) => {
        if (!open) prompt?.resolve(false); // escape / backdrop = cancel
      }}
    >
      <AlertDialogContent data-testid="gate-clear-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Clear {count} {noun}{forWhom}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            These are only shown while the relevant option is selected. Changing it hides — and clears — them:
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="list-disc pl-6 text-sm text-foreground space-y-0.5" data-testid="gate-clear-list">
          {prompt?.clears.map((c) => (
            <li key={c.fieldKey}>{c.label}</li>
          ))}
        </ul>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="gate-clear-cancel" onClick={() => prompt?.resolve(false)}>
            Keep them — don't change
          </AlertDialogCancel>
          <AlertDialogAction data-testid="gate-clear-continue" onClick={() => prompt?.resolve(true)}>
            Continue &amp; clear
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { writeWithGateClear, dialog };
}
